import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';

/**
 * Full-page status screens rendered instead of the app shell, while the app is
 * still deciding whether it can run at all (authenticating, bootstrapping) or
 * has decided it cannot.
 *
 * They are grouped here because they share one contract: a single
 * `langhub-status` element carrying the ARIA live-region role that matches the
 * state. Restating that contract at each call site is how announcement
 * semantics drift — an `alert` becoming a silent `status`, or two of them
 * rendering the same id.
 */

/**
 * Caps the failure Alert so it reads as a centred message rather than a banner
 * spanning the viewport. These screens have no shell around them to constrain width.
 */
const FAILURE_MAX_WIDTH = '42rem';

/** Blocking work-in-progress state; `role="status"` announces politely. */
export const LoadingStatus: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div id="langhub-status" role="status">
    <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
      {/*
        Stacked rather than laid out as inline siblings: a large spinner is
        taller than a line of text, so side by side the caption aligns to the
        spinner's baseline and reads as sitting too high.
      */}
      <SpaceBetween size="m" alignItems="center">
        <Spinner size="large" />
        <div>{children}</div>
      </SpaceBetween>
    </Box>
  </div>
);

/**
 * Blocking failure state, rendered as the same Cloudscape error `Alert` the pages
 * inside the shell use, so a failure that happens before the shell exists does not
 * look like it came from a different application.
 *
 * The `role="alert"` wrapper is load-bearing and must stay: Cloudscape's `Alert` sets
 * no ARIA role of its own, so dropping the div would silently downgrade a blocking
 * failure from an assertive announcement to no announcement at all.
 *
 * The padding and max width around the Alert are this component's job because these
 * screens render OUTSIDE `AppShell`, with no layout container to inherit either from;
 * without them the Alert reads as a banner across the whole viewport rather than a
 * message addressed to the user.
 *
 * Keep the copy to a header plus a line or two, and keep raw error text, codes
 * and other internals out of it: the console is where those belong.
 */
export const FailureStatus: React.FC<{
  heading: string;
  children: React.ReactNode;
}> = ({ heading, children }) => (
  <div id="langhub-status" role="alert">
    <Box padding="l">
      <div style={{ maxWidth: FAILURE_MAX_WIDTH, margin: '0 auto' }}>
        <Alert type="error" header={heading}>
          {children}
        </Alert>
      </div>
    </Box>
  </div>
);
